// Section 10: tools. One fungible stock with a mean durability (ADR-0009, B-02). Tools are drawn
// at the start of the season by the tool-allocation policy (decide phase, ADR-0010 item 21); a
// worker with a tool gets the job's tool multiplier (contributors/tools.ts). Each tool in use
// wears 0.1 per season (plus the job's extra wear); tools are destroyed when the mean durability
// reaches 0. Toolmaker output restores the durability of the tools in use first, then adds new
// tools at full durability. Without a toolmaker for 8+ seasons idle tools wear too, so the stock
// decays to zero (B-03). Constants: config.tools, config.jobs.*.toolFactor and toolWear.
import { round6 } from '../../core/math';
import { isWorking, peopleSorted } from '../../core/queries';
import type { Registry } from '../../core/registry';
import type { GameState, Person, PersonId, PhaseContext } from '../../core/types';
import { addStock } from './stocks';
import { num } from './value';

const EPS = 1e-9;

/** Section 10: a worker wants a tool when they work this season at a job with a tool multiplier. */
export function wantsTool(registry: Registry, p: Person): boolean {
  return p.job !== 'rest' && isWorking(p) && registry.jobs[p.job].toolFactor !== null;
}

/** Tools that can be handed out: whole tools on hand (a worn tool works until destroyed). */
export function toolsOnHand(state: GameState): number {
  return Math.floor(state.stocks.tools.amount + EPS);
}

/** Section 10 Tools panel: tools on hand weighted by their mean durability. */
export function effectiveTools(state: GameState): number {
  return state.stocks.tools.amount * state.toolDurability;
}

/** People as they will work this season: committed state plus this season's assignments. */
function withThisSeasonsJobs(ctx: PhaseContext): Person[] {
  const decision = ctx.draft.decisionLog[ctx.draft.decisionLog.length - 1];
  const out: Person[] = [];
  for (const p of peopleSorted(ctx.state)) {
    const job = decision?.assignments[p.id];
    out.push(job === undefined || job === p.job ? p : { ...p, job });
  }
  return out;
}

/**
 * "Highest output first": the output a tool would add, through the pipeline (output without a
 * tool, since nobody holds one yet this season, times the tool factor minus one).
 */
function toolGain(ctx: PhaseContext, p: Person): number {
  if (p.job === 'rest') return 0;
  const factor = ctx.registry.jobs[p.job].toolFactor ?? 1;
  const out = num(ctx, { kind: 'output', personId: p.id, jobId: p.job });
  return out * (factor - 1);
}

/** Order workers by the tool-allocation policy (Section 18); ties by id. */
function byPolicy(ctx: PhaseContext, workers: Person[], count: number): Person[] {
  const policy = ctx.draft.policies.toolAllocation;
  if (policy === 'manual') return workers.filter((p) => ctx.draft.toolOverrides[p.id] === true);
  if (policy === 'even-rotation') {
    // Everyone takes a turn: the window of `count` holders moves on by `count` each season.
    if (workers.length === 0) return workers;
    const offset = (ctx.season.turn * Math.max(1, count)) % workers.length;
    return [...workers.slice(offset), ...workers.slice(0, offset)];
  }
  const gain = new Map<PersonId, number>();
  for (const p of workers) gain.set(p.id, toolGain(ctx, p));
  const byGain = (a: Person, b: Person): number =>
    (gain.get(b.id) ?? 0) - (gain.get(a.id) ?? 0) || (a.id < b.id ? -1 : 1);
  const first =
    (job: string) =>
    (a: Person, b: Person): number =>
      Number(b.job === job) - Number(a.job === job) || byGain(a, b);
  switch (policy) {
    case 'farmers-first':
      return workers.sort(first('farmer'));
    case 'builders-first':
      return workers.sort(first('builder'));
    case 'highest-output':
      return workers.sort(byGain);
  }
}

/** Section 10: mark who holds a tool this season in `report.toolUse` (decide phase). */
export function allocateTools(ctx: PhaseContext): void {
  const report = ctx.report.current;
  const workers = withThisSeasonsJobs(ctx).filter((p) => wantsTool(ctx.registry, p));
  const available = toolsOnHand(ctx.draft);
  const holders =
    workers.length <= available && ctx.draft.policies.toolAllocation !== 'manual'
      ? workers
      : byPolicy(ctx, workers.slice(), available).slice(0, available);
  const chosen = new Set(holders.map((p) => p.id));
  const toolUse: Record<PersonId, boolean> = {};
  for (const p of workers) toolUse[p.id] = chosen.has(p.id);
  report.toolUse = toolUse;
  if (workers.length > available) {
    ctx.report.add({ kind: 'warning', code: 'tools-short', amount: workers.length - available });
  }
}

/** Section 10: wear `units` of durability off the stock; at mean durability 0 it is destroyed. */
export function wearTools(ctx: PhaseContext, units: number): number {
  const tools = ctx.draft.stocks.tools;
  if (tools.amount <= 0 || units <= 0) return 0;
  const before = ctx.draft.toolDurability;
  const after = Math.max(0, before - units / tools.amount);
  ctx.draft.toolDurability = after;
  const worn = (before - after) * tools.amount;
  if (after <= EPS) {
    addStock(ctx, 'tools', -tools.amount, 'other');
    ctx.draft.toolDurability = ctx.cfg.tools.startDurability;
  }
  return worn;
}

function toolsInUse(ctx: PhaseContext): number {
  let n = 0;
  for (const had of Object.values(ctx.report.current.toolUse)) if (had) n++;
  return Math.min(n, ctx.draft.stocks.tools.amount);
}

/**
 * Section 10: toolmaker output in tool units. It first restores the durability the tools in use
 * have lost, then becomes new tools at full durability (usable next season).
 */
export function makeTools(ctx: PhaseContext, units: number): void {
  if (units <= 0) return;
  const d = ctx.draft;
  const stock = d.stocks.tools;
  const deficit = Math.max(0, toolsInUse(ctx) * (1 - d.toolDurability));
  const repaired = Math.min(units, deficit);
  if (repaired > 0 && stock.amount > 0)
    d.toolDurability = Math.min(1, d.toolDurability + repaired / stock.amount);
  const made = units - repaired;
  if (made <= EPS) return;
  const before = stock.amount;
  d.toolDurability =
    (before * d.toolDurability + made * ctx.cfg.tools.startDurability) / (before + made);
  addStock(ctx, 'tools', made, 'production');
}

/** Seasons since anyone last worked as a toolmaker (since the start if never). */
export function seasonsWithoutToolmaker(state: GameState, turn: number): number {
  const last = state.lastWorkedTurn.toolmaker;
  return last === undefined ? turn : turn - last;
}

/** Wear for this season: tools in use (0.1 + job extra wear), and idle tools after 8+ seasons. */
function wearThisSeason(ctx: PhaseContext): number {
  let units = 0;
  let used = 0;
  for (const id of Object.keys(ctx.report.current.toolUse).sort() as PersonId[]) {
    if (ctx.report.current.toolUse[id] !== true) continue;
    const p = ctx.draft.people[id];
    if (p === undefined || p.job === 'rest') continue;
    used++;
    units += ctx.cfg.tools.wearPerUse + ctx.registry.jobs[p.job].toolWear;
  }
  const since = seasonsWithoutToolmaker(ctx.draft, ctx.season.turn);
  if (since >= ctx.cfg.tools.noToolmakerDecaySeasons) {
    const idle = Math.max(0, ctx.draft.stocks.tools.amount - used);
    units += idle * ctx.cfg.tools.wearPerUse;
  }
  return units;
}

/**
 * End of production (Section 10): report what toolmakers made and repaired this phase, then wear
 * the tools used this season. Repairs are measured as the durability units added beyond the
 * new tools, against the stock at the start of the phase.
 */
export function settleTools(ctx: PhaseContext): void {
  const start = ctx.state;
  const d = ctx.draft;
  const madeBefore = start.currentReport?.flows.tools.production ?? 0;
  const made = round6(Math.max(0, ctx.report.current.flows.tools.production - madeBefore));
  const unitsBefore = effectiveTools(start);
  const unitsNow = effectiveTools(d);
  const repaired = round6(Math.max(0, unitsNow - unitsBefore - made));
  const worn = round6(wearTools(ctx, wearThisSeason(ctx)));
  if (made > 0 || repaired > 0 || worn > 0) ctx.report.add({ kind: 'tools', made, repaired, worn });
}
